import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ExternalLink, Loader2, ShieldAlert, Wifi, WifiOff, Save, Edit3, Plug, PlugZap, Link2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simple official-style glyphs (SVG) so we don't depend on brand libs.
const TikTokIcon = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M19.6 8.3a6.6 6.6 0 01-3.9-1.3v6.8a5.4 5.4 0 11-4.6-5.4v2.7a2.7 2.7 0 102 2.6V2h2.6a4 4 0 003.9 3.9v2.4z" />
  </svg>
);
const InstagramIcon = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.3" cy="6.7" r="1" fill="currentColor" />
  </svg>
);
const YouTubeIcon = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M23 7.3a3 3 0 00-2.1-2.1C19 4.7 12 4.7 12 4.7s-7 0-8.9.5A3 3 0 001 7.3 31 31 0 00.6 12a31 31 0 00.4 4.7 3 3 0 002.1 2.1c1.9.5 8.9.5 8.9.5s7 0 8.9-.5a3 3 0 002.1-2.1c.3-1.6.4-3.1.4-4.7s-.1-3.2-.4-4.7zM9.8 15.5V8.5l6 3.5-6 3.5z" />
  </svg>
);

const PLATFORMS = [
  { id: 'tiktok',    label: 'TikTok',    Icon: TikTokIcon,    color: 'text-white' },
  { id: 'instagram', label: 'Instagram', Icon: InstagramIcon, color: 'text-white' },
  { id: 'youtube',   label: 'YouTube',   Icon: YouTubeIcon,   color: 'text-white' },
];

// ---------------------------------------------------------------------------
// Platform view drawer (profile / posts / metrics / comments / drafts / status)
// Empty placeholders shown when the account is not connected. Nothing is faked.
// ---------------------------------------------------------------------------
const PlatformView = ({ projectSlug, projectName, platform, socialData, onClose }) => {
  const p = PLATFORMS.find(x => x.id === platform);
  const Icon = p?.Icon;
  const url = socialData?.urls?.[platform] || '';
  const publicHandle = socialData?.handles?.[platform] || null;

  const [apiStatus, setApiStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/social/${projectSlug}/${platform}/status`, { credentials: 'include' });
      if (r.ok) setApiStatus(await r.json());
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [projectSlug, platform]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const disconnect = async () => {
    setBusy(true);
    try {
      await fetch(`${API}/social/${projectSlug}/${platform}/disconnect`, {
        method: 'POST', credentials: 'include',
      });
      await loadStatus();
    } finally { setBusy(false); }
  };

  const startConnect = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/social/${projectSlug}/${platform}/connect`, { credentials: 'include' });
      const data = await r.json();
      if (data.status === 'not_implemented' || data.status === 'requires_credentials') {
        // Refresh to display the message inline
        setApiStatus(prev => ({ ...(prev || {}), _connect_response: data }));
      }
    } finally { setBusy(false); }
  };

  const status = apiStatus?.status;
  const cred = apiStatus?.app_credentials;
  const account = apiStatus?.account;
  const connectedHandle = account?.account_handle || publicHandle;
  const connected = status === 'connected';

  const sections = [
    { key: 'profile',    label: 'Perfil / Cuenta',   endpoint: null },
    { key: 'posts',      label: 'Publicaciones',      endpoint: 'posts' },
    { key: 'metrics',    label: 'Métricas',           endpoint: 'metrics' },
    { key: 'comments',   label: 'Comentarios',        endpoint: 'comments' },
    { key: 'drafts',     label: 'Borradores',         endpoint: null },
    { key: 'pending',    label: 'Contenido pendiente', endpoint: null },
  ];

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      className="fixed top-0 right-0 h-full w-full sm:w-[520px] bg-[#0A0A0A] border-l border-neon-red/30 z-[60] flex flex-col shadow-[0_0_60px_rgba(255,0,51,0.25)]"
      data-testid={`platform-view-${projectSlug}-${platform}`}
    >
      <div className="flex items-center justify-between px-6 py-4 border-b border-neon-red/20 bg-black/40 backdrop-blur-xl">
        <div className="flex items-center gap-3 min-w-0">
          {Icon && <Icon className="w-5 h-5 text-neon-red" />}
          <div className="min-w-0">
            <h2 className="font-outfit text-lg font-bold text-white truncate">
              {projectName} · {p?.label}
            </h2>
            <p className="font-jetbrains text-[10px] text-white/50 tracking-widest uppercase truncate">
              {connected
                ? (connectedHandle ? `Conectada como ${connectedHandle}` : 'Cuenta conectada')
                : (status === 'requires_credentials'
                    ? 'Credenciales pendientes'
                    : (publicHandle ? `Perfil público ${publicHandle}` : 'Cuenta no conectada'))}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          data-testid={`platform-view-close-${projectSlug}-${platform}`}
          className="p-2 text-white/60 hover:text-white transition-colors"
          title="Cerrar"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {/* Connection status banner + connect/disconnect actions */}
        <div className={`p-4 border ${
          connected ? 'border-emerald-500/30 bg-emerald-500/5' :
          status === 'requires_credentials' ? 'border-amber-500/30 bg-amber-500/5' :
                                              'border-neon-red/25 bg-neon-red/[0.03]'
        }`}>
          <div className="flex items-start gap-3">
            {connected
              ? <Wifi className="w-4 h-4 text-emerald-400 mt-0.5" />
              : <WifiOff className="w-4 h-4 text-amber-400 mt-0.5" />}
            <div className="flex-1 min-w-0">
              {loading && (
                <p className="font-jetbrains text-xs text-white/60">Comprobando estado…</p>
              )}

              {!loading && status === 'requires_credentials' && (
                <>
                  <p className="font-jetbrains text-xs text-amber-300">Credenciales de app pendientes</p>
                  <p className="font-jetbrains text-[11px] text-white/60 mt-1">
                    Registra la app en {cred?.label} y añade estas variables a <span className="text-white/80">backend/.env</span>:
                  </p>
                  <ul className="mt-2 space-y-1.5">
                    {(cred?.missing || []).map(m => (
                      <li key={m.var} className="font-jetbrains text-[11px] text-white/70" data-testid={`cred-missing-${platform}-${m.var}`}>
                        <span className="text-neon-red">{m.var}</span>
                        <span className="text-white/40"> · {m.label} · </span>
                        <span className="text-white/50">{m.obtain}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="font-jetbrains text-[11px] text-white/50 mt-2">
                    Redirect URI sugerido: <code className="text-white/70">{cred?.suggested_redirect_uri}</code>
                  </p>
                  <div className="flex flex-wrap gap-3 mt-3">
                    {cred?.register_url && (
                      <a href={cred.register_url} target="_blank" rel="noopener noreferrer"
                         className="inline-flex items-center gap-1.5 font-jetbrains text-[11px] text-neon-red hover:text-white transition-colors">
                        <ExternalLink className="w-3 h-3" /> Registrar la app
                      </a>
                    )}
                    {cred?.doc_url && (
                      <a href={cred.doc_url} target="_blank" rel="noopener noreferrer"
                         className="inline-flex items-center gap-1.5 font-jetbrains text-[11px] text-white/60 hover:text-white transition-colors">
                        <ExternalLink className="w-3 h-3" /> Documentación oficial
                      </a>
                    )}
                  </div>
                </>
              )}

              {!loading && status === 'requires_connection' && (
                <>
                  <p className="font-jetbrains text-xs text-white/80">App configurada. Falta enlazar la cuenta.</p>
                  <p className="font-jetbrains text-[11px] text-white/50 mt-1">
                    Al pulsar Conectar iniciarás el flujo OAuth oficial. Nada se publica sin tu confirmación.
                  </p>
                  <button
                    onClick={startConnect} disabled={busy}
                    data-testid={`connect-account-${projectSlug}-${platform}`}
                    className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-neon-red/20 hover:bg-neon-red/30 border border-neon-red/50 hover:border-neon-red font-jetbrains text-[11px] tracking-widest uppercase text-white transition-colors disabled:opacity-50"
                  >
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plug className="w-3.5 h-3.5" />}
                    Conectar cuenta
                  </button>
                  {apiStatus?._connect_response?.message && (
                    <p className="mt-2 font-jetbrains text-[11px] text-amber-300">
                      {apiStatus._connect_response.message}
                    </p>
                  )}
                </>
              )}

              {!loading && connected && (
                <>
                  <p className="font-jetbrains text-xs text-emerald-300">Cuenta conectada</p>
                  <p className="font-jetbrains text-[11px] text-white/70 mt-1">
                    {connectedHandle ? `${connectedHandle}` : 'Cuenta enlazada'}
                    {account?.scopes?.length ? ` · Scopes: ${account.scopes.join(', ')}` : ''}
                  </p>
                  <button
                    onClick={disconnect} disabled={busy}
                    data-testid={`disconnect-account-${projectSlug}-${platform}`}
                    className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-black/40 hover:bg-red-500/10 border border-red-500/30 hover:border-red-500/60 font-jetbrains text-[11px] tracking-widest uppercase text-white transition-colors disabled:opacity-50"
                  >
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlugZap className="w-3.5 h-3.5" />}
                    Desconectar
                  </button>
                </>
              )}

              {url && (
                <a
                  href={url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-3 font-jetbrains text-[11px] text-white/60 hover:text-white transition-colors"
                  data-testid={`platform-view-open-external-${projectSlug}-${platform}`}
                >
                  <Link2 className="w-3 h-3" />
                  Abrir perfil público en {p?.label}
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Placeholder sections (structure ready for future data) */}
        {sections.map(s => (
          <div
            key={s.key}
            className="p-4 border border-neon-red/15 bg-[#0F0F0F]"
            data-testid={`platform-section-${projectSlug}-${platform}-${s.key}`}
          >
            <p className="font-jetbrains text-[10px] uppercase tracking-widest text-white/50">
              {s.label}
            </p>
            <p className="font-jetbrains text-xs text-white/40 mt-2 italic">
              {connected
                ? 'Cuenta enlazada. Los lectores oficiales se activan en la siguiente iteración con las credenciales aprobadas.'
                : status === 'requires_credentials'
                  ? 'Añade las credenciales indicadas arriba para desbloquear esta sección con datos reales.'
                  : 'Conecta la cuenta para ver esta sección con datos reales.'}
            </p>
          </div>
        ))}

        <p className="font-jetbrains text-[10px] text-white/30 leading-relaxed">
          Ningún dato mostrado aquí es simulado. Los espacios permanecen vacíos hasta que la API oficial esté configurada y confirmes explícitamente su activación.
        </p>
      </div>
    </motion.div>
  );
};

// ---------------------------------------------------------------------------
// URLs editor (per project) — small inline drawer.
// ---------------------------------------------------------------------------
const UrlsEditor = ({ projectSlug, initialData, onSaved, onClose }) => {
  const [tiktok,    setTiktok]    = useState(initialData?.urls?.tiktok    || '');
  const [instagram, setInstagram] = useState(initialData?.urls?.instagram || '');
  const [youtube,   setYoutube]   = useState(initialData?.urls?.youtube   || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setError(''); setSaving(true);
    try {
      const res = await fetch(`${API}/projects/${projectSlug}/social`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tiktok_url: tiktok.trim(),
          instagram_url: instagram.trim(),
          youtube_url: youtube.trim(),
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || 'No se pudo guardar');
      }
      const data = await res.json();
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setSaving(false);
    }
  };

  const Input = ({ label, value, onChange, testId, placeholder }) => (
    <div className="space-y-1">
      <label className="font-jetbrains text-[10px] uppercase tracking-widest text-white/50">
        {label}
      </label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full bg-black/40 border border-neon-red/20 focus:border-neon-red/60 outline-none font-jetbrains text-xs text-white px-3 py-2 transition-colors"
      />
    </div>
  );

  return (
    <div
      className="mt-3 p-4 bg-[#0F0F0F] border border-neon-red/25 space-y-3"
      data-testid={`urls-editor-${projectSlug}`}
    >
      <div className="flex items-center justify-between">
        <p className="font-jetbrains text-[10px] uppercase tracking-widest text-white/70">
          URLs públicas del proyecto
        </p>
        <button
          onClick={onClose}
          className="text-white/50 hover:text-white"
          data-testid={`urls-editor-close-${projectSlug}`}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <Input label="TikTok"    value={tiktok}    onChange={setTiktok}    testId={`url-input-tiktok-${projectSlug}`}    placeholder="https://www.tiktok.com/@..." />
      <Input label="Instagram" value={instagram} onChange={setInstagram} testId={`url-input-instagram-${projectSlug}`} placeholder="https://www.instagram.com/..." />
      <Input label="YouTube"   value={youtube}   onChange={setYoutube}   testId={`url-input-youtube-${projectSlug}`}   placeholder="https://www.youtube.com/@..." />
      {error && (
        <div className="flex items-start gap-2 p-2 border border-red-500/30 bg-red-500/5">
          <ShieldAlert className="w-3.5 h-3.5 text-red-400 mt-0.5" />
          <p className="font-jetbrains text-[11px] text-red-300">{error}</p>
        </div>
      )}
      <button
        onClick={save}
        disabled={saving}
        data-testid={`urls-editor-save-${projectSlug}`}
        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-neon-red/20 hover:bg-neon-red/30 border border-neon-red/50 hover:border-neon-red font-jetbrains text-xs tracking-widest uppercase text-white transition-colors disabled:opacity-50"
      >
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
        {saving ? 'Guardando...' : 'Guardar URLs'}
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main compact row of platform buttons + optional views.
// ---------------------------------------------------------------------------
const SocialSelector = ({ projectSlug, projectName }) => {
  const [data, setData] = useState(null);
  const [overview, setOverview] = useState(null);  // per-platform api status (connected/requires_*)
  const [loading, setLoading] = useState(true);
  const [openPlatform, setOpenPlatform] = useState(null);
  const [editing, setEditing] = useState(false);
  const [connectBusy, setConnectBusy] = useState({});   // { platform: bool }

  const loadUrls = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/projects/${projectSlug}/social`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [projectSlug]);

  const loadOverview = useCallback(async () => {
    try {
      const r = await fetch(`${API}/social/overview`, { credentials: 'include' });
      if (r.ok) {
        const j = await r.json();
        setOverview(j?.projects?.[projectSlug] || null);
      }
    } catch (e) { /* silent */ }
  }, [projectSlug]);

  useEffect(() => { loadUrls(); loadOverview(); }, [loadUrls, loadOverview]);

  // Auto-refresh overview when the tab regains focus (typical when the user
  // finishes OAuth in a popup and returns to AliVan).
  useEffect(() => {
    const onFocus = () => loadOverview();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadOverview]);

  const openProfile = (platform) => {
    const url = data?.urls?.[platform];
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
    else setOpenPlatform(platform);
  };

  const openDrawer = (platform) => setOpenPlatform(platform);

  const startConnect = async (platform) => {
    setConnectBusy(s => ({ ...s, [platform]: true }));
    try {
      const r = await fetch(`${API}/social/${projectSlug}/${platform}/connect`, { credentials: 'include' });
      const data = await r.json();
      if (data.status === 'ready' && data.authorize_url) {
        window.open(data.authorize_url, '_blank', 'noopener,noreferrer,width=560,height=720');
      } else {
        setOpenPlatform(platform);
      }
    } catch (e) {
      setOpenPlatform(platform);
    } finally {
      setConnectBusy(s => ({ ...s, [platform]: false }));
    }
  };

  const disconnect = async (platform) => {
    setConnectBusy(s => ({ ...s, [platform]: true }));
    try {
      await fetch(`${API}/social/${projectSlug}/${platform}/disconnect`, {
        method: 'POST', credentials: 'include',
      });
      await loadOverview();
    } finally {
      setConnectBusy(s => ({ ...s, [platform]: false }));
    }
  };

  // Per-platform "open profile" label (YouTube uses "Abrir canal", others "Abrir perfil").
  const openLabel = (id) => id === 'youtube' ? 'Abrir canal' : 'Abrir perfil';

  return (
    <div className="px-5 py-3 border-b border-neon-red/10 space-y-2.5" data-testid={`social-selector-${projectSlug}`}>
      <div className="flex items-center justify-between">
        <p className="font-jetbrains text-[10px] uppercase tracking-widest text-white/50">Redes</p>
        <button
          onClick={() => setEditing(v => !v)}
          title="Editar URLs del proyecto"
          data-testid={`social-edit-urls-${projectSlug}`}
          className="flex items-center gap-1 px-2 py-1 text-white/50 hover:text-neon-red transition-colors"
        >
          <Edit3 className="w-3 h-3" />
          <span className="font-jetbrains text-[10px] tracking-widest uppercase">URLs</span>
        </button>
      </div>

      {PLATFORMS.map(({ id, label, Icon }) => {
        const info = overview?.[id];
        const apiConnected = info?.connected === true;
        const apiHandle = info?.account_handle;
        const publicHandle = data?.handles?.[id];
        const publicUrl = data?.urls?.[id];
        const displayHandle = apiHandle || publicHandle || 'sin cuenta configurada';
        const reason = info?.reason;
        const busy = !!connectBusy[id];
        const connectDisabled = reason === 'requires_credentials';

        return (
          <div
            key={id}
            className={`p-3 bg-black/40 border ${apiConnected ? 'border-emerald-500/40' : 'border-neon-red/20'} space-y-2`}
            data-testid={`social-row-${projectSlug}-${id}`}
          >
            <div className="flex items-center gap-2">
              <Icon className="w-4 h-4 text-white/80" />
              <span className="font-jetbrains text-[11px] uppercase tracking-widest text-white/80">{label}</span>
              <span className={`ml-auto font-jetbrains text-[11px] ${apiConnected ? 'text-emerald-400' : (publicHandle ? 'text-white/70' : 'text-white/40')}`}
                    data-testid={`social-handle-${projectSlug}-${id}`}>
                {apiConnected ? `Conectada como ${apiHandle || 'canal'}` : displayHandle}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => openProfile(id)}
                disabled={!publicUrl}
                data-testid={`open-profile-${projectSlug}-${id}`}
                title={publicUrl ? `${openLabel(id)} de ${label}` : `Configura primero la URL pública en el editor URLs`}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 border transition-colors font-jetbrains text-[10px] tracking-widest uppercase ${
                  publicUrl
                    ? 'bg-black/40 hover:bg-neon-red/10 border-neon-red/40 hover:border-neon-red text-white/80 hover:text-white'
                    : 'bg-black/30 border-white/15 text-white/40 cursor-not-allowed'
                }`}
              >
                <ExternalLink className="w-3 h-3" />
                {openLabel(id)}
              </button>
              {apiConnected ? (
                <button
                  onClick={() => disconnect(id)}
                  disabled={busy}
                  data-testid={`row-disconnect-${projectSlug}-${id}`}
                  title={`Desconectar cuenta de ${label}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-500/10 hover:bg-red-500/10 border border-emerald-500/40 hover:border-red-500/60 text-emerald-300 hover:text-red-200 transition-colors font-jetbrains text-[10px] tracking-widest uppercase disabled:opacity-50"
                >
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlugZap className="w-3 h-3" />}
                  Desconectar
                </button>
              ) : (
                <button
                  onClick={() => startConnect(id)}
                  disabled={busy || connectDisabled}
                  data-testid={`row-connect-${projectSlug}-${id}`}
                  title={connectDisabled ? `Faltan credenciales para ${label}` : `Conectar cuenta de ${label}`}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 border transition-colors font-jetbrains text-[10px] tracking-widest uppercase ${
                    connectDisabled
                      ? 'bg-black/30 border-white/15 text-white/40 cursor-not-allowed'
                      : 'bg-black/40 hover:bg-neon-red/10 border-neon-red/40 hover:border-neon-red text-white/80 hover:text-white'
                  }`}
                >
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plug className="w-3 h-3" />}
                  Conectar cuenta
                </button>
              )}
              <button
                onClick={() => openDrawer(id)}
                data-testid={`social-btn-${projectSlug}-${id}`}
                title={`Detalles de ${label}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-black/30 hover:bg-black/60 border border-white/15 hover:border-white/40 text-white/60 hover:text-white transition-colors font-jetbrains text-[10px] tracking-widest uppercase"
              >
                Detalles
              </button>
            </div>
          </div>
        );
      })}

      {editing && (
        <UrlsEditor
          projectSlug={projectSlug}
          initialData={data}
          onSaved={(newData) => setData(newData)}
          onClose={() => setEditing(false)}
        />
      )}

      <AnimatePresence>
        {openPlatform && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[55]"
              onClick={() => { setOpenPlatform(null); loadOverview(); }}
              data-testid={`platform-view-backdrop-${projectSlug}-${openPlatform}`}
            />
            <PlatformView
              projectSlug={projectSlug}
              projectName={projectName}
              platform={openPlatform}
              socialData={data}
              onClose={() => { setOpenPlatform(null); loadOverview(); }}
            />
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SocialSelector;
